/** Light / dark theme (D54). The choice is a per-phone convenience kept in localStorage; Auto (the
 *  default, stored as "no choice") follows the phone. index.html applies it before first paint with
 *  the same key and rule, so this module only keeps it in sync afterwards. */

export type ThemePref = 'auto' | 'light' | 'dark';
export type Theme = 'light' | 'dark';

const KEY = 'sb.theme.v1';
/** Browser chrome / status bar color per theme — the page background in theme.css. */
const CHROME_COLOR: Record<Theme, string> = { light: '#f4f5f6', dark: '#0c0d0e' };

export const resolveTheme = (pref: ThemePref, systemDark: boolean): Theme =>
  pref === 'auto' ? (systemDark ? 'dark' : 'light') : pref;

export const loadThemePref = (): ThemePref => {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'auto';
  } catch {
    return 'auto';
  }
};

/** Without the media query (old engines, tests) the app keeps its original dark look. */
const systemPrefersDark = (): boolean => window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? true;

export const currentTheme = (): Theme =>
  document.documentElement.dataset.theme === 'light' ? 'light' : 'dark';

const applyTheme = (pref: ThemePref): void => {
  const theme = resolveTheme(pref, systemPrefersDark());
  document.documentElement.dataset.theme = theme;
  document
    .querySelector<HTMLMetaElement>('meta[name="theme-color"]')
    ?.setAttribute('content', CHROME_COLOR[theme]);
};

export const setThemePref = (pref: ThemePref): void => {
  try {
    if (pref === 'auto') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, pref);
  } catch {
    // storage blocked: the choice still applies until the app is closed
  }
  applyTheme(pref);
};

/** Apply the saved choice and keep Auto in step when the phone flips between light and dark. */
export const initTheme = (): void => {
  applyTheme(loadThemePref());
  window.matchMedia?.('(prefers-color-scheme: dark)').addEventListener?.('change', () => {
    if (loadThemePref() === 'auto') applyTheme('auto');
  });
};
